import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { MoneyInput } from '../../Currency'
import { DatePicker } from '../../DateTime'
import { FieldStackedLabel } from '../../Field'
import { FormFieldSelectNG } from '../../Form'
import { FormikField, getFormikFormFieldErrorProps } from '../../Formik'
import { InputField, InputMode } from '../../Input'
import { InputSelect } from '../../Select'
import { SupplierSelect } from '../../Suppliers'
import { TextareaOutlineField } from '../../Textarea'
import {
  INVOICE_FIELD_NAME_NOTES,
  INVOICE_FIELD_TYPE_DATE,
  INVOICE_FIELD_TYPE_FLOAT,
  INVOICE_FIELD_TYPE_INT,
  INVOICE_FIELD_TYPE_SELECT,
  INVOICE_FIELD_TYPE_SUPPLIER,
  INVOICE_MODAL_FIELD_AMOUNT_FRACTION_DIGITS,
} from '../InvoiceModal.constants'
import {
  checkIsBicField,
  checkIsIbanField,
  getInvoiceModalFieldMaxLength,
} from '../InvoiceModal.utils'

import {
  INVOICE_MODAL_SELECT_DROPDOWN_FLOATING_CONFIG,
  INVOICE_MODAL_SUPPLIER_SELECT_DROPDOWN_FLOATING_CONFIG,
} from './InvoiceModalFormField.constants'
import {
  getBankAccountFieldNextValue,
  getFieldComponentProps,
  getFieldRenderType,
} from './InvoiceModalFormField.utils'

import * as styles from './InvoiceModalFormField.module.css'

function InvoiceModalFormField({ disabled, field }) {
  const { t } = useTranslation()

  const label = t(`DocumentMarkAsInvoice.${field.label}Key`)
  const placeholder = t(`DocumentMarkAsInvoice.${field.label}PlaceholderKey`)
  const fieldProps = getFieldComponentProps({
    disabled,
    field,
    label,
    placeholder,
  })

  const maxLength = getInvoiceModalFieldMaxLength(field)

  switch (getFieldRenderType(field)) {
    case INVOICE_FIELD_TYPE_DATE:
      return (
        <FormikField
          {...fieldProps}
          Component={DatePicker}
          Field={FieldStackedLabel}
          wrapperClassName={styles.datePickerWrap}
        />
      )
    case INVOICE_FIELD_TYPE_SUPPLIER:
      return (
        <FormikField
          {...fieldProps}
          Component={FormFieldSelectNG}
          Field={FieldStackedLabel}
          Select={SupplierSelect}
          controlProps={{ contentProps: { inputProps: { placeholder } } }}
          dropdownFloatingConfig={
            INVOICE_MODAL_SUPPLIER_SELECT_DROPDOWN_FLOATING_CONFIG
          }
          emptyValue={null}
          getErrorProps={getFormikFormFieldErrorProps}
        />
      )
    case INVOICE_FIELD_NAME_NOTES:
      return (
        <FormikField
          {...fieldProps}
          Component={TextareaOutlineField}
          Field={FieldStackedLabel}
        />
      )
    case INVOICE_FIELD_TYPE_SELECT:
      return (
        <FormikField
          {...fieldProps}
          Component={FormFieldSelectNG}
          Field={FieldStackedLabel}
          Select={InputSelect}
          controlProps={{ contentProps: { inputProps: { placeholder } } }}
          dropdownFloatingConfig={INVOICE_MODAL_SELECT_DROPDOWN_FLOATING_CONFIG}
          emptyValue={null}
          getErrorProps={getFormikFormFieldErrorProps}
          options={field.options}
        />
      )
    case INVOICE_FIELD_TYPE_FLOAT:
      return (
        <FormikField
          {...fieldProps}
          Component={MoneyInput}
          Field={FieldStackedLabel}
          getErrorProps={getFormikFormFieldErrorProps}
          minimumFractionDigits={INVOICE_MODAL_FIELD_AMOUNT_FRACTION_DIGITS}
        />
      )
    case INVOICE_FIELD_TYPE_INT:
      return (
        <FormikField
          Component={InputField}
          Field={Fragment}
          disabled={disabled}
          inputProps={{ inputMode: InputMode.Decimal }}
          label={label}
          name={field.name}
          placeholder={placeholder}
          required={field.required}
        />
      )

    default: {
      const isBankAccountField =
        checkIsBicField(field) || checkIsIbanField(field)

      return (
        <FormikField
          Component={InputField}
          Field={Fragment}
          disabled={disabled}
          getNextValue={
            isBankAccountField ? getBankAccountFieldNextValue : undefined
          }
          hasCounter={maxLength != null}
          label={label}
          maxLength={maxLength}
          name={field.name}
          placeholder={placeholder}
          required={field.required}
        />
      )
    }
  }
}

export default InvoiceModalFormField
